import React, { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Building2, Globe, Upload, Plus, X, Save, Truck,
  FileText, CheckCircle, AlertTriangle, Image, ShieldOff, Loader2,
  Palette, RotateCcw, Tag, MapPin
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { useBranding } from '@/context/BrandingContext';
import { extractColorsFromImage } from '@/utils/colorExtractor';
import { useCurrency, CURRENCIES } from '@/context/CurrencyContext';
import InfoTip from '@/components/InfoTip';
import api from '@/services/api';

const DEFAULT_DEPARTMENTS = ['Procurement', 'Engineering', 'Quality', 'R&D', 'Finance', 'Operations', 'Supply Chain', 'Management'];
const DEFAULT_COUNTRIES = ['USA', 'China', 'Germany', 'India', 'Taiwan', 'Japan', 'Vietnam', 'South Korea', 'Global'];

const CompanySettings = () => {
  const { branding, updateBranding, resetBranding } = useBranding();
  const { currency, changeCurrency, symbol } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState({
    name: '', industry: '', country: '', exportCountry: 'Global',
    address: '', website: '', taxId: ''
  });
  const [approvedSuppliers, setApprovedSuppliers] = useState([]);
  const [blacklistedSuppliers, setBlacklistedSuppliers] = useState([]);
  const [newSupplier, setNewSupplier] = useState({ name: '', type: 'authorized', region: 'Global', list_type: 'approved' });
  const [showAddSupplier, setShowAddSupplier] = useState(false);

  // Editable departments & countries (persisted in localStorage)
  const [departments, setDepartments] = useState(() => {
    const saved = localStorage.getItem('1data_departments');
    return saved ? JSON.parse(saved) : DEFAULT_DEPARTMENTS;
  });
  const [countries, setCountries] = useState(() => {
    const saved = localStorage.getItem('1data_countries');
    return saved ? JSON.parse(saved) : DEFAULT_COUNTRIES;
  });
  const [newDept, setNewDept] = useState('');
  const [newCountry, setNewCountry] = useState('');

  const industries = ['Consumer Electronics', 'Automotive', 'Medical Devices', 'Industrial', 'Aerospace', 'Telecommunications'];

  useEffect(() => {
    localStorage.setItem('1data_departments', JSON.stringify(departments));
  }, [departments]);

  useEffect(() => {
    localStorage.setItem('1data_countries', JSON.stringify(countries));
  }, [countries]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, suppliersRes] = await Promise.all([
        api.get('/company/settings'),
        api.get('/company/suppliers'),
      ]);
      const s = settingsRes.data.settings || {};
      setCompany({
        name: s.name || '', industry: s.industry || '', country: s.country || '',
        exportCountry: s.export_country || 'Global', address: s.address || '',
        website: s.website || '', taxId: s.tax_id || '',
      });
      setApprovedSuppliers(suppliersRes.data.approved || []);
      setBlacklistedSuppliers(suppliersRes.data.blacklisted || []);
    } catch {
      // First load - defaults are fine
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompany = async () => {
    setSaving(true);
    try {
      await api.post('/company/settings', company);
      toast.success('Company settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target.result;
      updateBranding({ logoUrl: dataUrl });
      toast.success('Logo updated');

      // Extract colors from logo
      const colors = await extractColorsFromImage(dataUrl);
      if (colors) {
        updateBranding({ primaryColor: colors.primary, accentColor: colors.accent });
        toast.success('Color scheme extracted from logo');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleAddSupplier = async () => {
    if (!newSupplier.name) { toast.error('Please enter supplier name'); return; }
    try {
      await api.post('/company/suppliers', newSupplier);
      toast.success(`Supplier added to ${newSupplier.list_type} list`);
      setNewSupplier({ name: '', type: 'authorized', region: 'Global', list_type: 'approved' });
      setShowAddSupplier(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add supplier');
    }
  };

  const handleRemoveSupplier = async (supplierId) => {
    try {
      await api.delete(`/company/suppliers/${supplierId}`);
      toast.success('Supplier removed');
      loadData();
    } catch {
      toast.error('Failed to remove supplier');
    }
  };

  const addDepartment = () => {
    const d = newDept.trim();
    if (!d) return;
    if (departments.includes(d)) { toast.error('Department already exists'); return; }
    setDepartments([...departments, d]);
    setNewDept('');
    toast.success(`Department "${d}" added`);
  };

  const removeDepartment = (dept) => {
    setDepartments(departments.filter(d => d !== dept));
    toast.success(`Department "${dept}" removed`);
  };

  const addCountry = () => {
    const c = newCountry.trim();
    if (!c) return;
    if (countries.includes(c)) { toast.error('Country/Region already exists'); return; }
    setCountries([...countries, c]);
    setNewCountry('');
    toast.success(`Country/Region "${c}" added`);
  };

  const removeCountry = (country) => {
    setCountries(countries.filter(c => c !== country));
    toast.success(`Country/Region "${country}" removed`);
  };

  if (loading) {
    return (
      <Layout title="Company Settings" subtitle="Configure organization profile and approved suppliers">
        <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>
      </Layout>
    );
  }

  return (
    <Layout title="Company Settings" subtitle="Configure organization profile and approved suppliers">
      <div className="space-y-6" data-testid="company-settings-page">
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-5 max-w-2xl">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="branding">Branding</TabsTrigger>
            <TabsTrigger value="filters">Filters</TabsTrigger>
            <TabsTrigger value="approved">Approved</TabsTrigger>
            <TabsTrigger value="compliance">Compliance</TabsTrigger>
          </TabsList>

          {/* Company Profile */}
          <TabsContent value="profile" className="mt-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  Company Profile
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Display Name (shown in sidebar)</Label>
                  <Input value={branding.companyName} onChange={(e) => updateBranding({ companyName: e.target.value })}
                    placeholder="Your company name" className="mt-1 max-w-md" data-testid="company-display-name" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label>Company Name</Label>
                    <Input value={company.name} onChange={e => setCompany({ ...company, name: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label>Industry</Label>
                    <Select value={company.industry} onValueChange={v => setCompany({ ...company, industry: v })}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select industry" /></SelectTrigger>
                      <SelectContent>{industries.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Headquarters Country</Label>
                    <Select value={company.country} onValueChange={v => setCompany({ ...company, country: v })}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select country" /></SelectTrigger>
                      <SelectContent>{countries.filter(c => c !== 'Global').map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Primary Export Destination</Label>
                    <Select value={company.exportCountry} onValueChange={v => setCompany({ ...company, exportCountry: v })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Address</Label>
                    <Textarea value={company.address} onChange={e => setCompany({ ...company, address: e.target.value })} className="mt-1" rows={2} />
                  </div>
                  <div>
                    <Label>Website</Label>
                    <Input value={company.website} onChange={e => setCompany({ ...company, website: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label>Tax ID</Label>
                    <Input value={company.taxId} onChange={e => setCompany({ ...company, taxId: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      Preferred Display Currency
                      <InfoTip text="All data stored in USD. Converted at display time using cached daily exchange rates." />
                    </Label>
                    <Select value={currency} onValueChange={changeCurrency}>
                      <SelectTrigger className="mt-1" data-testid="currency-selector">
                        <SelectValue>{symbol} {currency}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map(c => (
                          <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-gray-400 mt-1">All pricing data stored in USD. Converted at display time.</p>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleSaveCompany} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Branding — Logo + Color Scheme */}
          <TabsContent value="branding" className="mt-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <Palette className="h-5 w-5 text-purple-600" />
                  Branding & Color Scheme
                </CardTitle>
                <CardDescription>Upload your logo to auto-extract a color scheme, or set colors manually.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Logo Upload */}
                <div className="p-4 border rounded-lg bg-gray-50">
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center bg-white overflow-hidden">
                      {branding.logoUrl ? (
                        <img src={branding.logoUrl} alt="Logo" className="w-full h-full object-contain p-1" />
                      ) : (
                        <Image className="h-8 w-8 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">Company Logo</h4>
                      <p className="text-sm text-gray-500">Upload to auto-extract brand colors. Recommended: 200x200px</p>
                      <div className="flex gap-2 mt-2">
                        <Label htmlFor="logo-upload" className="cursor-pointer">
                          <span className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200 transition-colors">
                            <Upload className="h-3 w-3" /> Upload Logo
                          </span>
                          <input id="logo-upload" type="file" accept="image/*" className="hidden" data-testid="logo-upload-input"
                            onChange={handleLogoUpload}
                          />
                        </Label>
                        {branding.logoUrl && (
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => { updateBranding({ logoUrl: null }); toast.success('Logo removed'); }}>
                            <X className="h-3 w-3 mr-1" /> Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Color Scheme */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="mb-2 block">Primary Color</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={branding.primaryColor}
                        onChange={(e) => updateBranding({ primaryColor: e.target.value })}
                        className="w-12 h-10 rounded border cursor-pointer"
                        data-testid="primary-color-picker"
                      />
                      <Input
                        value={branding.primaryColor}
                        onChange={(e) => updateBranding({ primaryColor: e.target.value })}
                        className="w-32 font-mono text-sm"
                        data-testid="primary-color-input"
                      />
                      <div className="w-24 h-10 rounded-lg" style={{ backgroundColor: branding.primaryColor }} />
                    </div>
                  </div>
                  <div>
                    <Label className="mb-2 block">Accent Color</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={branding.accentColor}
                        onChange={(e) => updateBranding({ accentColor: e.target.value })}
                        className="w-12 h-10 rounded border cursor-pointer"
                        data-testid="accent-color-picker"
                      />
                      <Input
                        value={branding.accentColor}
                        onChange={(e) => updateBranding({ accentColor: e.target.value })}
                        className="w-32 font-mono text-sm"
                        data-testid="accent-color-input"
                      />
                      <div className="w-24 h-10 rounded-lg" style={{ backgroundColor: branding.accentColor }} />
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="p-4 border rounded-lg bg-gray-50">
                  <h4 className="font-medium text-gray-900 mb-3">Preview</h4>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow" style={{ background: `linear-gradient(135deg, ${branding.primaryColor}, ${branding.accentColor})` }}>
                      <span className="text-white font-bold text-lg">{(branding.companyName || '1D')[0]}</span>
                    </div>
                    <div>
                      <p className="font-bold" style={{ color: branding.primaryColor }}>{branding.companyName || '1DATA'}</p>
                      <p className="text-xs text-gray-500">powered by 1BUY.AI</p>
                    </div>
                    <div className="ml-auto flex gap-2">
                      <Button size="sm" style={{ backgroundColor: branding.primaryColor, color: '#fff' }}>Primary Button</Button>
                      <Button size="sm" variant="outline" style={{ borderColor: branding.accentColor, color: branding.accentColor }}>Accent Button</Button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => { resetBranding(); toast.success('Colors reset to default'); }} data-testid="reset-branding-btn">
                    <RotateCcw className="h-4 w-4 mr-2" /> Reset to Default
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Editable Departments & Countries */}
          <TabsContent value="filters" className="mt-6 space-y-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <Tag className="h-5 w-5 text-blue-600" />
                  Departments
                </CardTitle>
                <CardDescription>Manage department names used across filters and action assignments.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  <Input
                    value={newDept}
                    onChange={(e) => setNewDept(e.target.value)}
                    placeholder="Add new department..."
                    className="max-w-xs"
                    data-testid="new-department-input"
                    onKeyDown={(e) => e.key === 'Enter' && addDepartment()}
                  />
                  <Button onClick={addDepartment} className="bg-green-600 hover:bg-green-700 text-white" data-testid="add-department-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="departments-list">
                  {departments.map((dept) => (
                    <Badge key={dept} className="bg-blue-50 text-blue-800 border border-blue-200 px-3 py-1.5 text-sm flex items-center gap-1.5">
                      {dept}
                      <button onClick={() => removeDepartment(dept)} className="text-blue-400 hover:text-red-500 transition-colors">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-green-600" />
                  Countries & Regions
                </CardTitle>
                <CardDescription>Manage countries and regions available in dropdown filters across the platform. You can add custom entries like "World Wide".</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  <Input
                    value={newCountry}
                    onChange={(e) => setNewCountry(e.target.value)}
                    placeholder='Add country or region (e.g., "World Wide")...'
                    className="max-w-xs"
                    data-testid="new-country-input"
                    onKeyDown={(e) => e.key === 'Enter' && addCountry()}
                  />
                  <Button onClick={addCountry} className="bg-green-600 hover:bg-green-700 text-white" data-testid="add-country-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="countries-list">
                  {countries.map((country) => (
                    <Badge key={country} className="bg-green-50 text-green-800 border border-green-200 px-3 py-1.5 text-sm flex items-center gap-1.5">
                      {country}
                      <button onClick={() => removeCountry(country)} className="text-green-400 hover:text-red-500 transition-colors">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Approved & Blacklisted Suppliers */}
          <TabsContent value="approved" className="mt-6 space-y-6">
            <SupplierList
              title="Approved Suppliers"
              icon={<Truck className="h-5 w-5 text-green-600" />}
              description="Manage your empaneled supplier list for BOM analysis"
              suppliers={approvedSuppliers}
              listType="approved"
              showAdd={showAddSupplier}
              setShowAdd={setShowAddSupplier}
              newSupplier={newSupplier}
              setNewSupplier={setNewSupplier}
              onAdd={handleAddSupplier}
              onRemove={handleRemoveSupplier}
            />
            <SupplierList
              title="Blacklisted Suppliers"
              icon={<ShieldOff className="h-5 w-5 text-red-600" />}
              description="Suppliers to be excluded from all sourcing recommendations"
              suppliers={blacklistedSuppliers}
              listType="blacklisted"
              showAdd={showAddSupplier}
              setShowAdd={setShowAddSupplier}
              newSupplier={newSupplier}
              setNewSupplier={setNewSupplier}
              onAdd={handleAddSupplier}
              onRemove={handleRemoveSupplier}
            />
          </TabsContent>

          {/* Compliance */}
          <TabsContent value="compliance" className="mt-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-amber-600" />
                  Export Compliance Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <h4 className="font-medium text-gray-900">Active Compliance Checks</h4>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li>RoHS 3 Compliance</li>
                      <li>REACH SVHC Screening</li>
                      <li>Conflict Minerals (3TG)</li>
                      <li>ITAR/EAR Restrictions</li>
                      <li>Country of Origin Verification</li>
                    </ul>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600" />
                      <h4 className="font-medium text-gray-900">Export Restrictions</h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Based on your export country ({company.exportCountry}), the following apply:
                    </p>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li>Dual-use items screening required</li>
                      <li>End-user verification for sensitive components</li>
                      <li>Export license documentation</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

const SupplierList = ({ title, icon, description, suppliers, listType, showAdd, setShowAdd, newSupplier, setNewSupplier, onAdd, onRemove }) => (
  <Card className="bg-white border-gray-200 shadow-sm">
    <CardHeader>
      <div className="flex items-center justify-between">
        <div>
          <CardTitle className="text-gray-900 flex items-center gap-2">{icon}{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        <Button onClick={() => { setNewSupplier(prev => ({ ...prev, list_type: listType })); setShowAdd(true); }}
          className={listType === 'blacklisted' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}>
          <Plus className="h-4 w-4 mr-2" /> Add Supplier
        </Button>
      </div>
    </CardHeader>
    <CardContent>
      {showAdd && newSupplier.list_type === listType && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Supplier Name</Label>
              <Input value={newSupplier.name} onChange={e => setNewSupplier({ ...newSupplier, name: e.target.value })} placeholder="Enter name" className="mt-1" data-testid="new-supplier-name" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={newSupplier.type} onValueChange={v => setNewSupplier({ ...newSupplier, type: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="authorized">Authorized</SelectItem>
                  <SelectItem value="independent">Independent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Region</Label>
              <Select value={newSupplier.region} onValueChange={v => setNewSupplier({ ...newSupplier, region: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Global">Global</SelectItem>
                  <SelectItem value="Americas">Americas</SelectItem>
                  <SelectItem value="Europe">Europe</SelectItem>
                  <SelectItem value="Asia">Asia</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={onAdd} className={listType === 'blacklisted' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}>Add</Button>
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {suppliers.length === 0 ? (
        <div className="py-8 text-center text-gray-500">
          <p>No {listType} suppliers yet. Add one to get started.</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Supplier</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Region</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {suppliers.map(s => (
              <TableRow key={s.supplier_id} data-testid={`supplier-row-${s.supplier_id}`}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell>
                  <Badge className={s.type === 'authorized' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
                    {s.type}
                  </Badge>
                </TableCell>
                <TableCell>{s.region}</TableCell>
                <TableCell>
                  <Badge className={s.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                    {s.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => onRemove(s.supplier_id)}>
                    <X className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
);

export default CompanySettings;
